<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        Edit Consultation
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Modify consultation details, appointments, and status
                        information
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                    >
                      <v-icon left>{{
                        isEditMode ? "mdi-eye" : "mdi-pencil"
                      }}</v-icon>
                      {{ isEditMode ? "Read Only" : "Edit Mode" }}
                    </v-btn>

                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Consultations
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Consultation Information</h2>
              <v-spacer></v-spacer>
              <v-chip
                :color="isEditMode ? 'success' : 'warning'"
                :outlined="!isEditMode"
                x-small
              >
                <v-icon left x-small>{{
                  isEditMode ? "mdi-pencil" : "mdi-eye"
                }}</v-icon>
                {{ isEditMode ? "Edit Mode" : "Read Only Mode" }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>
            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <div v-if="!consultation && !isLoading" class="text-center">
                <v-icon large color="error">mdi-alert-circle</v-icon>
                <p class="mt-2">Consultation not found</p>
                <v-btn color="primary" @click="$router.back()">Go Back</v-btn>
              </div>
              <div v-else>
                <v-row>
                  <v-col sm="12">
                    <v-text-field
                      v-model="form.title"
                      outlined
                      label="Consultation Title"
                      prepend-inner-icon="mdi-format-title"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.title"
                      placeholder="Enter consultation title..."
                    ></v-text-field>
                  </v-col>
                  <v-col sm="6">
                    <v-autocomplete
                      v-model="form.doctor_id"
                      :items="doctors"
                      item-text="text"
                      item-value="value"
                      outlined
                      label="Select Doctor"
                      prepend-inner-icon="mdi-doctor"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :clearable="isEditMode"
                      :error-messages="errors.doctor_id"
                    ></v-autocomplete>
                  </v-col>
                  <v-col sm="6">
                    <v-autocomplete
                      v-model="form.patient_id"
                      :items="patients"
                      item-text="text"
                      item-value="value"
                      outlined
                      label="Select Patient"
                      prepend-inner-icon="mdi-account-heart"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :clearable="isEditMode"
                      :error-messages="errors.patient_id"
                    ></v-autocomplete>
                  </v-col>
                  <v-col sm="6">
                    <v-autocomplete
                      v-model="form.type_id"
                      :items="consultationTypes"
                      outlined
                      label="Consultation Type"
                      prepend-inner-icon="mdi-medical-bag"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :clearable="isEditMode"
                      :error-messages="errors.type_id"
                    ></v-autocomplete>
                  </v-col>
                  <v-col sm="6">
                    <v-text-field
                      v-model="form.appointment_id"
                      outlined
                      label="Appointment ID (Optional)"
                      prepend-inner-icon="mdi-calendar"
                      type="number"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.appointment_id"
                    ></v-text-field>
                  </v-col>
                  <v-col sm="6">
                    <v-text-field
                      v-model="form.start_time"
                      outlined
                      label="Start Time"
                      prepend-inner-icon="mdi-clock-start"
                      type="datetime-local"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.start_time"
                    ></v-text-field>
                  </v-col>
                  <v-col sm="6">
                    <v-text-field
                      v-model="form.end_time"
                      outlined
                      label="End Time"
                      prepend-inner-icon="mdi-clock-end"
                      type="datetime-local"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.end_time"
                    ></v-text-field>
                  </v-col>
                  <v-col sm="6">
                    <v-autocomplete
                      v-model="form.status"
                      :items="statusOptions"
                      outlined
                      label="Status"
                      prepend-inner-icon="mdi-check-circle"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.status"
                    ></v-autocomplete>
                  </v-col>
                  <v-col sm="12">
                    <v-textarea
                      v-model="form.notes"
                      outlined
                      label="Consultation Notes"
                      rows="4"
                      prepend-inner-icon="mdi-note-text"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.notes"
                      auto-grow
                      :hint="
                        isEditMode
                          ? 'Add any additional notes or observations for the consultation'
                          : ''
                      "
                    ></v-textarea>
                  </v-col>
                  <v-col sm="12">
                    <v-textarea
                      v-model="form.doctor_notes"
                      outlined
                      label="Doctor Notes"
                      rows="4"
                      prepend-inner-icon="mdi-doctor"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.doctor_notes"
                      auto-grow
                      :hint="isEditMode ? 'Private notes added by the doctor for this consultation' : ''"
                    ></v-textarea>
                  </v-col>
                </v-row>

                <!-- Consultation Information Card -->
                <v-card class="mt-4" outlined>
                  <v-card-title>
                    <v-icon left>mdi-information</v-icon>
                    Consultation Information
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col sm="3">
                        <strong>Created:</strong><br />
                        {{ formatDate(consultation.created_at) }}
                      </v-col>
                      <v-col sm="3">
                        <strong>Last Updated:</strong><br />
                        {{ formatDate(consultation.updated_at) }}
                      </v-col>
                      <v-col sm="3">
                        <strong>Created By:</strong><br />
                        {{
                          consultation.created_by
                            ? consultation.created_by.user_name
                            : "N/A"
                        }}
                      </v-col>
                      <v-col sm="3">
                        <strong>Consultation Status:</strong><br />
                        <v-chip
                          small
                          :color="getStatusColor(form.status)"
                          text-color="white"
                        >
                          {{ getStatusText(form.status) }}
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <!-- Form Actions Footer -->
                <v-divider></v-divider>
                <v-card-actions class="form-actions pa-2" v-show="isEditMode">
                  <v-row class="w-100 ma-0" align="center">
                    <v-col cols="12" md="6" class="pa-0">
                      <div class="d-flex align-center">
                        <span class="text-caption grey--text">
                          All required fields must be completed
                        </span>
                      </div>
                    </v-col>

                    <v-col cols="12" md="6" class="pa-0">
                      <div class="d-flex justify-end">
                        <v-btn
                          color="error"
                          outlined
                          class="mr-3 action-button"
                          @click="confirmDelete"
                          :disabled="isSaving"
                        >
                          <v-icon left>mdi-delete</v-icon>
                          Delete
                        </v-btn>
                        <v-btn
                          color="grey"
                          outlined
                          class="mr-3 action-button"
                          @click="$router.back()"
                          :disabled="isSaving"
                        >
                          Cancel
                        </v-btn>
                        <v-btn
                          color="primary"
                          class="action-button"
                          @click="updateConsultation"
                          :loading="isSaving"
                          :disabled="isSaving"
                        >
                          <v-icon left>mdi-content-save</v-icon>
                          Update Consultation
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-actions>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Consultation Lifecycle Card -->
      <v-row v-if="!isLoading">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="py-2 px-4">
              <v-icon color="primary" class="mr-2" size="20">mdi-timeline-clock</v-icon>
              <span class="text-body-1 font-weight-medium">Consultation Lifecycle</span>
              <v-spacer></v-spacer>
              <status-chip x-small :color="consultationLifecycle.find(s => s.active)?.color || 'grey'" :label="String(consultationLifecycle.find(s => s.active)?.label || '-')" />
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="px-6 py-5">
              <div class="d-flex align-center" style="overflow-x: auto; min-height: 80px;">
                <template v-for="(step, i) in consultationLifecycle">
                  <div :key="`step-${i}`" class="d-flex flex-column align-center" style="min-width: 90px;">
                    <v-avatar
                      :color="step.active ? step.color : step.done ? step.color : 'grey lighten-3'"
                      size="38"
                    >
                      <v-icon :color="step.done || step.active ? 'white' : 'grey lighten-1'" small>
                        {{ step.icon }}
                      </v-icon>
                    </v-avatar>
                    <span
                      class="text-caption text-center mt-1 font-weight-medium"
                      :class="step.active ? `${step.color}--text` : step.done ? 'primary--text' : 'grey--text'"
                      style="max-width: 85px; line-height: 1.2;"
                    >{{ step.label }}</span>
                  </div>
                  <div
                    v-if="i < consultationLifecycle.length - 1"
                    :key="`line-${i}`"
                    style="flex: 1; height: 2px; min-width: 24px; margin-bottom: 20px; border-radius: 1px;"
                    :style="{ background: consultationLifecycle[i + 1].done || consultationLifecycle[i + 1].active ? '#1976D2' : '#E0E0E0' }"
                  ></div>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading State -->
      <v-row v-if="isLoading">
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <v-card-text class="py-12 text-center">
              <health-loader message="Loading consultation details..." />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Consultation updated successfully.</h3>
                      <br />
                      <v-btn color="success" @click="showSuccessDialog = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: red; font-size: 90px; width: 200px !important"
                >
                  mdi-alert-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: red">Confirm Delete</h1>
                      <br />
                      <h3>
                        Are you sure you want to delete this consultation?
                      </h3>
                      <br />
                      <v-btn
                        color="error"
                        @click="deleteConsultation"
                        :loading="isDeleting"
                        class="mr-4"
                      >
                        Yes, Delete
                      </v-btn>
                      <v-btn
                        color="secondary"
                        @click="showDeleteDialog = false"
                        :disabled="isDeleting"
                      >
                        Cancel
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "ConsultationEdit",
  data() {
    return {
      consultation: null,
      isSaving: false,
      isDeleting: false,
      isLoading: true,
      showSuccessDialog: false,
      showDeleteDialog: false,
      doctors: [],
      patients: [],
      errors: {},
      isEditMode: false,

      form: {
        title: "",
        doctor_id: null,
        patient_id: null,
        type_id: null,
        appointment_id: null,
        start_time: "",
        end_time: "",
        status: false,
        is_active: true,
        notes: "",
        doctor_notes: "",
      },

      consultationTypes: [
        { value: 1, text: "General Consultation" },
        { value: 2, text: "Follow-up" },
        { value: 3, text: "Specialist Consultation" },
        { value: 4, text: "Emergency Consultation" },
        { value: 5, text: "Routine Check-up" },
      ],

      statusOptions: [
        { value: false, text: "Pending" },
        { value: true, text: "Completed" },
      ],
    };
  },

  mounted() {
    this.fetchConsultation();
    this.getMasters();
  },

  computed: {
    consultationLifecycle() {
      const s = parseInt(this.form.status ?? -1);
      const terminalMap = {
        3: { label: 'Cancelled', icon: 'mdi-close-circle', color: 'error' },
        4: { label: 'Missed',    icon: 'mdi-account-off',  color: 'grey darken-1' },
      };
      const isTerminal = [3, 4].includes(s);
      const steps = [
        { label: 'Scheduled',   icon: 'mdi-calendar-clock', color: 'blue',    done: s > 0,               active: s === 0 },
        { label: 'In Progress', icon: 'mdi-video',          color: 'orange',  done: s === 2 || isTerminal, active: s === 1 },
      ];
      if (isTerminal) {
        const t = terminalMap[s];
        steps.push({ label: t.label, icon: t.icon, color: t.color, done: false, active: true });
      } else {
        steps.push({ label: 'Completed', icon: 'mdi-check-circle', color: 'success', done: s === 2, active: s === 2 });
      }
      return steps;
    },
  },

  methods: {
    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      // Show a message to the user about the mode change
      if (this.isEditMode) {
        this.$toast.success("Edit mode enabled. You can now modify the form.");
      } else {
        this.$toast.info(
          "Read-only mode enabled. Form is now protected from changes."
        );
      }
    },

    async getMasters() {
      try {
        const response = await axios.get("masters?context=consultations");

        if (response.data?.users) {
          // Filter doctors
          this.doctors = response.data.users
            .filter(
              (user) =>
                user.position?.name?.toLowerCase().includes("doctor") ||
                user.position?.name?.toLowerCase().includes("physician") ||
                user.position?.name?.toLowerCase().includes("specialist") ||
                user.position?.name?.toLowerCase().includes("consultant") ||
                user.position?.name?.toLowerCase().includes("therapist") ||
                user.position?.name?.toLowerCase().includes("practitioner")
            )
            .map((user) => ({
              text: user.user_name,
              value: user.id,
            }));

          // Filter patients
          this.patients = response.data.users
            .filter(
              (user) =>
                user.position?.name?.toLowerCase().includes("patient") ||
                user.position?.name?.toLowerCase().includes("client") ||
                user.position?.name?.toLowerCase().includes("member") ||
                user.position?.name?.toLowerCase().includes("customer") ||
                user.position?.name?.toLowerCase().includes("user")
            )
            .map((user) => ({
              text: user.user_name,
              value: user.id,
            }));

          // If no users found with specific positions, show all users as fallback
          if (this.doctors.length === 0) {
            this.doctors = response.data.users.map((user) => ({
              text: user.user_name,
              value: user.id,
            }));
            console.warn(
              "No doctors found with specific positions, showing all users"
            );
          }
          if (this.patients.length === 0) {
            this.patients = response.data.users.map((user) => ({
              text: user.user_name,
              value: user.id,
            }));
            console.warn(
              "No patients found with specific positions, showing all users"
            );
          }
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },

    async fetchConsultation() {
      this.isLoading = true;
      try {
        const consultationId = this.$route.params.id;
        const response = await axios.get(`/consultations/${consultationId}`);

        if (response.data.success) {
          this.consultation = response.data.data;
          this.form = {
            title: this.consultation.title || "",
            doctor_id: this.consultation.doctor_id,
            patient_id: this.consultation.patient_id,
            type_id: this.consultation.type_id,
            appointment_id: this.consultation.appointment_id,
            start_time: this.consultation.start_time
              ? this.formatDateTimeForInput(this.consultation.start_time)
              : "",
            end_time: this.consultation.end_time
              ? this.formatDateTimeForInput(this.consultation.end_time)
              : "",
            status: this.consultation.status,
            is_active: this.consultation.is_active,
            notes: this.consultation.notes || "",
            doctor_notes: this.consultation.doctor_notes || "",
          };
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Consultation not found",
          });
        }
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 404) {
          this.$store.dispatch("errors/setError", {
            message: "Consultation not found",
          });
        } else if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error loading consultation data",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async updateConsultation() {
      this.isSaving = true;
      this.errors = {};

      try {
        const consultationData = {
          title: this.form.title,
          doctor_id: this.form.doctor_id,
          patient_id: this.form.patient_id,
          type_id: this.form.type_id,
          appointment_id: this.form.appointment_id,
          start_time: this.form.start_time,
          end_time: this.form.end_time,
          status: this.form.status,
          is_active: this.form.is_active,
          notes: this.form.notes,
          doctor_notes: this.form.doctor_notes,
        };

        const consultationId = this.$route.params.id;
        const response = await axios.patch(
          `/consultations/${consultationId}`,
          consultationData
        );

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.consultation = response.data.data;
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while updating the consultation.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            this.$router.push("/login");
          } else {
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while updating the consultation.",
            });
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    confirmDelete() {
      this.showDeleteDialog = true;
    },

    async deleteConsultation() {
      this.isDeleting = true;

      try {
        const consultationId = this.$route.params.id;
        const response = await axios.post(
          `/consultations/delete/${consultationId}`
        );

        if (response.data.success) {
          this.showDeleteDialog = false;
          this.$router.push("/consultations");
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while deleting the consultation.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "An error occurred while deleting the consultation.",
          });
        }
      } finally {
        this.isDeleting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },

    formatDateTime(dateTime) {
      if (!dateTime) return "N/A";
      try {
        return new Date(dateTime).toLocaleString();
      } catch {
        return "Invalid Date";
      }
    },

    formatDateTimeForInput(dateTime) {
      if (!dateTime) return "";
      try {
        const date = new Date(dateTime);
        return date.toISOString().slice(0, 16);
      } catch {
        return "";
      }
    },

    getStatusColor(status) {
      return status ? "success" : "warning";
    },

    getStatusText(status) {
      return status ? "Completed" : "Pending";
    },
  },
};
</script>

<style scoped>
/* Professional Page Header */
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

/* Professional Form Card */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin: 0;
}

.form-content {
  background: #ffffff;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

.v-card-title {
  font-weight: bold;
  font-size: 1.5rem;
}
</style>